import React from 'react';
import { useLocation } from 'react-router';
import { TabulatedPageTabWithValue } from 'ui/TabulatedPage/TabulatedPage';

// TODO: this hook was previously shared with src/routes/BudgetModule/components/shared/TabulatedChipButtonGroupBudget/hooks.tsx
// We should probably have a single tabulation logic, and compose the components to have either a button, a tab or a chip.

/**
 * This hook is used to know if a tab has already been visited or not. On first render, only the active
 * tab is considered as visited. When the user clicks on a tab, it is considered as visited. We keep track of previously
 * visited tabs in order to keep them in the DOM. This is an optimization to avoid re-rendering the tab content.
 * @param tabsList list of tabs
 * @param keepSearch if true, the search part of the URL is kept in the pathname
 * @return object telling for each tab if it has already been visited or not.
 */
export const useGetVisitedTabs = (
    tabsListWithTabValue: TabulatedPageTabWithValue[],
    activeTabValue: string,
): { [path: string]: boolean } => {
    const visitedTabsInitValue = tabsListWithTabValue.reduce<
        Record<string, boolean>
    >(
        (result, currentTab) => ({
            ...result,
            [currentTab.value]: false,
        }),
        {},
    );
    const [visitedTabs, setVisitedTabs] =
        React.useState<Record<string, boolean>>(visitedTabsInitValue);
    React.useEffect(() => {
        setVisitedTabs((tabs) => ({
            ...tabs,
            [activeTabValue]: true,
        }));
    }, [activeTabValue]);
    return visitedTabs;
};

/**
 * This hook is used to know which tab is active based on the URL.
 * @param tabsListWithTabValue list of tabs
 * @param keepSearch if true, the search part of the URL is kept in the pathname
 * @return active tab value
 */
export const useGetActiveTab = (
    tabsListWithTabValue: TabulatedPageTabWithValue[],
    keepSearch: boolean = false,
): string => {
    const location = useLocation();
    const search = location.search ?? '';
    // if there is no search in the URL and some tabs do have one, we match the first tab by default
    const shouldMatchFirstTabByDefault =
        !search && tabsListWithTabValue.some((tab) => tab.search);
    if (shouldMatchFirstTabByDefault) {
        return (
            tabsListWithTabValue[0].pathname + tabsListWithTabValue[0].search
        );
    }
    if (keepSearch) return location.pathname + search;

    // pick the tab whose params are all in the URL, extra ones (e.g. detail panel id) ignored
    const urlParams = new URLSearchParams(search);
    const matchingTab = tabsListWithTabValue.find(
        (tab) =>
            tab.search &&
            tab.pathname === location.pathname &&
            [...new URLSearchParams(tab.search)].every(
                ([key, value]) => urlParams.get(key) === value,
            ),
    );
    return matchingTab?.value ?? location.pathname;
};
